import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  FileText,
  Bell,
  CreditCard,
  Calendar,
  FolderOpen,
  Briefcase,
  UserPlus,
  Eye,
  RotateCcw,
  Save,
  Check,
  Info,
  MessageSquare,
  Mail,
  Smartphone,
  Clock,
  ChevronRight,
  Plus,
  X,
  AlertCircle,
} from "lucide-react";
import { messageTemplatesApi, reminderScheduleApi, twilioApi } from "../../services/api";
import PremiumModal from "../ui/PremiumModal";

// ─── Types ───────────────────────────────────────────────────────────────────

interface TemplateVariable {
  key: string;
  label: string;
  sample: string;
}

interface TemplateConfig {
  key: string;
  name: string;
  description: string;
  icon: React.FC<any>;
  variables: TemplateVariable[];
}

interface TemplateData {
  key: string;
  emailSubject: string;
  bodyText: string;
  smsMessage: string;
  isCustomized: boolean;
}

interface ReminderScheduleData {
  invoiceReminders: {
    enabled: boolean;
    firstReminderHours: number;
    followUpDays: number;
    maxReminders: number;
    channels: { email: boolean; sms: boolean };
    sendOnWeekends: boolean;
  };
  appointmentReminders: {
    enabled: boolean;
    reminderTimes: number[];
    channels: { email: boolean; sms: boolean };
  };
}

// ─── Template Definitions ────────────────────────────────────────────────────

const TEMPLATE_CONFIGS: TemplateConfig[] = [
  {
    key: "invoice_sent",
    name: "Invoice Sent",
    description: "Sent when a new invoice is delivered",
    icon: FileText,
    variables: [
      { key: "clientName", label: "Client Name", sample: "John Smith" },
      { key: "invoiceNumber", label: "Invoice Number", sample: "INV-00042" },
      { key: "totalAmount", label: "Total Amount", sample: "$1,500.00" },
      { key: "dueDate", label: "Due Date", sample: "April 15, 2026" },
      { key: "firmName", label: "Firm Name", sample: "Smith & Associates" },
      { key: "paymentLink", label: "Payment Link", sample: "https://pay.kavoa.io/abc123" },
    ],
  },
  {
    key: "invoice_reminder",
    name: "Invoice Reminder",
    description: "Automated reminders for unpaid invoices",
    icon: Bell,
    variables: [
      { key: "clientName", label: "Client Name", sample: "John Smith" },
      { key: "invoiceNumber", label: "Invoice Number", sample: "INV-00042" },
      { key: "totalAmount", label: "Total Amount", sample: "$1,500.00" },
      { key: "balanceDue", label: "Balance Due", sample: "$1,500.00" },
      { key: "dueDate", label: "Due Date", sample: "April 15, 2026" },
      { key: "overdueStatus", label: "Overdue Status", sample: "3 days overdue" },
      { key: "firmName", label: "Firm Name", sample: "Smith & Associates" },
      { key: "paymentLink", label: "Payment Link", sample: "https://pay.kavoa.io/abc123" },
    ],
  },
  {
    key: "payment_received",
    name: "Payment Received",
    description: "Confirmation when payment is received",
    icon: CreditCard,
    variables: [
      { key: "clientName", label: "Client Name", sample: "John Smith" },
      { key: "invoiceNumber", label: "Invoice Number", sample: "INV-00042" },
      { key: "totalAmount", label: "Total Amount", sample: "$1,500.00" },
      { key: "paymentDate", label: "Payment Date", sample: "April 10, 2026" },
      { key: "firmName", label: "Firm Name", sample: "Smith & Associates" },
    ],
  },
  {
    key: "appointment_reminder",
    name: "Appointment Reminder",
    description: "Before scheduled appointments",
    icon: Calendar,
    variables: [
      { key: "clientName", label: "Client Name", sample: "John Smith" },
      { key: "appointmentDate", label: "Date", sample: "April 15, 2026" },
      { key: "appointmentTime", label: "Time", sample: "2:00 PM" },
      { key: "location", label: "Location", sample: "123 Main St, Suite 200" },
      { key: "firmName", label: "Firm Name", sample: "Smith & Associates" },
      { key: "lawyerName", label: "Lawyer Name", sample: "Jane Doe" },
    ],
  },
  {
    key: "document_request",
    name: "Document Request",
    description: "When requesting documents from clients",
    icon: FolderOpen,
    variables: [
      { key: "clientName", label: "Client Name", sample: "John Smith" },
      { key: "documentList", label: "Document List", sample: "Government-issued ID, Proof of address" },
      { key: "matterTitle", label: "Matter Title", sample: "Smith v. Johnson" },
      { key: "firmName", label: "Firm Name", sample: "Smith & Associates" },
      { key: "dueDate", label: "Due Date", sample: "April 15, 2026" },
    ],
  },
  {
    key: "matter_update",
    name: "Matter Update",
    description: "Case status update notifications",
    icon: Briefcase,
    variables: [
      { key: "clientName", label: "Client Name", sample: "John Smith" },
      { key: "matterTitle", label: "Matter Title", sample: "Smith v. Johnson" },
      { key: "updateSummary", label: "Update Summary", sample: "Your case has moved to the discovery phase." },
      { key: "firmName", label: "Firm Name", sample: "Smith & Associates" },
      { key: "lawyerName", label: "Lawyer Name", sample: "Jane Doe" },
    ],
  },
  {
    key: "welcome_client",
    name: "Welcome Client",
    description: "Welcome message for new clients",
    icon: UserPlus,
    variables: [
      { key: "clientName", label: "Client Name", sample: "John Smith" },
      { key: "firmName", label: "Firm Name", sample: "Smith & Associates" },
      { key: "lawyerName", label: "Lawyer Name", sample: "Jane Doe" },
      { key: "portalLink", label: "Portal Link", sample: "https://app.kavoa.io/portal" },
    ],
  },
];

const DEFAULT_TEMPLATES: Record<string, TemplateData> = {
  invoice_sent: {
    key: "invoice_sent",
    emailSubject: "Invoice {{invoiceNumber}} from {{firmName}}",
    bodyText: "Dear {{clientName}},\n\nPlease find attached invoice {{invoiceNumber}} for {{totalAmount}}.\n\nPayment is due by {{dueDate}}. You can pay securely online using the link below:\n\n{{paymentLink}}\n\nIf you have any questions, please don't hesitate to reach out.\n\nBest regards,\n{{firmName}}",
    smsMessage: "Hi {{clientName}}, invoice {{invoiceNumber}} for {{totalAmount}} is ready. Due: {{dueDate}}. Pay online: {{paymentLink}}",
    isCustomized: false,
  },
  invoice_reminder: {
    key: "invoice_reminder",
    emailSubject: "Reminder: Invoice {{invoiceNumber}} — {{overdueStatus}}",
    bodyText: "Dear {{clientName}},\n\nThis is a friendly reminder that invoice {{invoiceNumber}} for {{balanceDue}} is {{overdueStatus}}.\n\nOriginal due date: {{dueDate}}\n\nPlease make your payment at your earliest convenience:\n\n{{paymentLink}}\n\nIf you've already sent payment, please disregard this notice.\n\nBest regards,\n{{firmName}}",
    smsMessage: "Reminder: Invoice {{invoiceNumber}} ({{balanceDue}}) is {{overdueStatus}}. Pay now: {{paymentLink}}",
    isCustomized: false,
  },
  payment_received: {
    key: "payment_received",
    emailSubject: "Payment Received — Invoice {{invoiceNumber}}",
    bodyText: "Dear {{clientName}},\n\nThank you for your payment of {{totalAmount}} received on {{paymentDate}} for invoice {{invoiceNumber}}.\n\nYour account is now up to date.\n\nBest regards,\n{{firmName}}",
    smsMessage: "Hi {{clientName}}, we've received your payment of {{totalAmount}} for invoice {{invoiceNumber}}. Thank you!",
    isCustomized: false,
  },
  appointment_reminder: {
    key: "appointment_reminder",
    emailSubject: "Appointment Reminder — {{appointmentDate}} at {{appointmentTime}}",
    bodyText: "Dear {{clientName}},\n\nThis is a reminder of your upcoming appointment:\n\nDate: {{appointmentDate}}\nTime: {{appointmentTime}}\nLocation: {{location}}\nWith: {{lawyerName}}\n\nPlease arrive 10 minutes early. If you need to reschedule, please contact us as soon as possible.\n\nBest regards,\n{{firmName}}",
    smsMessage: "Reminder: You have an appointment on {{appointmentDate}} at {{appointmentTime}} with {{lawyerName}} at {{location}}. — {{firmName}}",
    isCustomized: false,
  },
  document_request: {
    key: "document_request",
    emailSubject: "Documents Requested — {{matterTitle}}",
    bodyText: "Dear {{clientName}},\n\nFor your matter \"{{matterTitle}}\", we need the following documents:\n\n{{documentList}}\n\nPlease provide these by {{dueDate}}.\n\nYou can upload documents securely through your client portal or reply to this email with attachments.\n\nBest regards,\n{{firmName}}",
    smsMessage: "Hi {{clientName}}, we need documents for {{matterTitle}} by {{dueDate}}. Check your email for details. — {{firmName}}",
    isCustomized: false,
  },
  matter_update: {
    key: "matter_update",
    emailSubject: "Update on {{matterTitle}}",
    bodyText: "Dear {{clientName}},\n\nWe have an update regarding your matter \"{{matterTitle}}\":\n\n{{updateSummary}}\n\nIf you have any questions, please contact {{lawyerName}}.\n\nBest regards,\n{{firmName}}",
    smsMessage: "Hi {{clientName}}, update on {{matterTitle}}: {{updateSummary}} — {{firmName}}",
    isCustomized: false,
  },
  welcome_client: {
    key: "welcome_client",
    emailSubject: "Welcome to {{firmName}}",
    bodyText: "Dear {{clientName}},\n\nWelcome to {{firmName}}! We're pleased to have you as a client.\n\nYour primary contact will be {{lawyerName}}. You can access your client portal anytime at:\n\n{{portalLink}}\n\nDon't hesitate to reach out if you have any questions.\n\nWarm regards,\n{{firmName}}",
    smsMessage: "Welcome to {{firmName}}, {{clientName}}! Your lawyer {{lawyerName}} looks forward to working with you. Access your portal: {{portalLink}}",
    isCustomized: false,
  },
};

const SAMPLE_VARIABLES: Record<string, string> = {
  clientName: "John Smith",
  invoiceNumber: "INV-00042",
  totalAmount: "$1,500.00",
  dueDate: "April 15, 2026",
  firmName: "Smith & Associates",
  paymentLink: "https://pay.kavoa.io/abc123",
  balanceDue: "$1,500.00",
  overdueStatus: "3 days overdue",
  paymentDate: "April 10, 2026",
  appointmentDate: "April 15, 2026",
  appointmentTime: "2:00 PM",
  location: "123 Main St, Suite 200",
  lawyerName: "Jane Doe",
  documentList: "Government-issued ID, Proof of address",
  matterTitle: "Smith v. Johnson",
  updateSummary: "Your case has moved to the discovery phase.",
  portalLink: "https://app.kavoa.io/portal",
};

const APPOINTMENT_PRESETS = [
  { label: "30 min", value: 30 },
  { label: "1 hour", value: 60 },
  { label: "2 hours", value: 120 },
  { label: "12 hours", value: 720 },
  { label: "24 hours", value: 1440 },
];

// ─── Helper ──────────────────────────────────────────────────────────────────

function renderWithVariables(text: string, vars: Record<string, string>): string {
  return text.replace(/\{\{(\w+)\}\}/g, (_, key) => vars[key] || `{{${key}}}`);
}

function formatMinutes(mins: number): string {
  if (mins < 60) return `${mins}min`;
  if (mins < 1440) return `${mins / 60}h`;
  return `${mins / 1440}d`;
}

// ─── Component ───────────────────────────────────────────────────────────────

const MessageTemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"templates" | "reminders">("templates");
  const [selectedKey, setSelectedKey] = useState(TEMPLATE_CONFIGS[0].key);
  const [templates, setTemplates] = useState<Record<string, TemplateData>>(DEFAULT_TEMPLATES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTab, setPreviewTab] = useState<"email" | "sms">("email");
  const [previewHtml, setPreviewHtml] = useState("");

  // Reminder schedule state
  const [reminderSchedule, setReminderSchedule] = useState<ReminderScheduleData>({
    invoiceReminders: {
      enabled: true,
      firstReminderHours: 24,
      followUpDays: 3,
      maxReminders: 3,
      channels: { email: true, sms: false },
      sendOnWeekends: false,
    },
    appointmentReminders: {
      enabled: true,
      reminderTimes: [1440, 60],
      channels: { email: true, sms: true },
    },
  });
  const [reminderSaving, setReminderSaving] = useState(false);
  const [reminderSaved, setReminderSaved] = useState(false);
  const [customTimeInput, setCustomTimeInput] = useState("");
  const [hasPhoneNumbers, setHasPhoneNumbers] = useState<boolean | null>(null);

  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const smsRef = useRef<HTMLTextAreaElement>(null);

  const selectedConfig = TEMPLATE_CONFIGS.find((t) => t.key === selectedKey)!;
  const selectedTemplate = templates[selectedKey];

  // ─── Load data ──────────────────────────────────────────────────────────────

  useEffect(() => {
    const load = async () => {
      try {
        const [tmplRes, schedRes, phoneRes] = await Promise.allSettled([
          messageTemplatesApi.list(),
          reminderScheduleApi.get(),
          twilioApi.listNumbers({ limit: 1 }),
        ]);
        if (tmplRes.status === "fulfilled" && tmplRes.value.data) {
          const serverTemplates: Record<string, TemplateData> = {};
          const items = Array.isArray(tmplRes.value.data) ? tmplRes.value.data : tmplRes.value.data.data || [];
          items.forEach((t: any) => {
            serverTemplates[t.key] = t;
          });
          setTemplates((prev) => ({ ...prev, ...serverTemplates }));
        }
        if (schedRes.status === "fulfilled" && schedRes.value.data) {
          const d = schedRes.value.data.data || schedRes.value.data;
          if (d.invoiceReminders) setReminderSchedule(d);
        }
        if (phoneRes.status === "fulfilled") {
          const numbers = phoneRes.value.data?.data || [];
          setHasPhoneNumbers(numbers.length > 0);
        } else {
          setHasPhoneNumbers(false);
        }
      } catch {
        // Use defaults
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // ─── Template actions ───────────────────────────────────────────────────────

  const updateField = (field: keyof TemplateData, value: string) => {
    setTemplates((prev) => ({
      ...prev,
      [selectedKey]: { ...prev[selectedKey], [field]: value, isCustomized: true },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await messageTemplatesApi.update(selectedKey, {
        emailSubject: selectedTemplate.emailSubject,
        bodyText: selectedTemplate.bodyText,
        smsMessage: selectedTemplate.smsMessage,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      await messageTemplatesApi.reset(selectedKey);
      setTemplates((prev) => ({
        ...prev,
        [selectedKey]: { ...DEFAULT_TEMPLATES[selectedKey], isCustomized: false },
      }));
    } catch {
      // silent
    }
  };

  const handlePreview = async () => {
    setPreviewOpen(true);
    try {
      const res = await messageTemplatesApi.preview(selectedKey, SAMPLE_VARIABLES);
      if (res.data?.html) {
        setPreviewHtml(res.data.html);
      } else {
        setPreviewHtml(
          `<div style="font-family: -apple-system, BlinkMacSystemFont, sans-serif; padding: 40px; max-width: 600px; margin: 0 auto;">
            <div style="background: #0F5132; color: white; padding: 24px 32px; border-radius: 12px 12px 0 0; text-align: center;">
              <h2 style="margin: 0; font-size: 20px;">Smith & Associates</h2>
            </div>
            <div style="background: white; border: 1px solid #e2e8f0; border-top: none; padding: 32px; border-radius: 0 0 12px 12px;">
              <p style="font-size: 14px; color: #374151; white-space: pre-wrap; line-height: 1.7;">${renderWithVariables(selectedTemplate.bodyText, SAMPLE_VARIABLES).replace(/\n/g, "<br/>")}</p>
            </div>
          </div>`
        );
      }
    } catch {
      setPreviewHtml(
        `<div style="font-family: -apple-system, BlinkMacSystemFont, sans-serif; padding: 40px; max-width: 600px; margin: 0 auto;">
          <div style="background: #0F5132; color: white; padding: 24px 32px; border-radius: 12px 12px 0 0; text-align: center;">
            <h2 style="margin: 0; font-size: 20px;">Smith & Associates</h2>
          </div>
          <div style="background: white; border: 1px solid #e2e8f0; border-top: none; padding: 32px; border-radius: 0 0 12px 12px;">
            <p style="font-size: 14px; color: #374151; white-space: pre-wrap; line-height: 1.7;">${renderWithVariables(selectedTemplate.bodyText, SAMPLE_VARIABLES).replace(/\n/g, "<br/>")}</p>
          </div>
        </div>`
      );
    }
  };

  const insertVariable = useCallback((varKey: string, target: "subject" | "body" | "sms") => {
    const insertion = `{{${varKey}}}`;
    const ref = target === "subject" ? subjectRef : target === "body" ? bodyRef : smsRef;
    const el = ref.current;
    if (el) {
      const start = el.selectionStart ?? el.value.length;
      const end = el.selectionEnd ?? start;
      const newVal = el.value.slice(0, start) + insertion + el.value.slice(end);
      const field = target === "subject" ? "emailSubject" : target === "body" ? "bodyText" : "smsMessage";
      updateField(field as keyof TemplateData, newVal);
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(start + insertion.length, start + insertion.length);
      });
    }
  }, [selectedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Reminder actions ───────────────────────────────────────────────────────

  const handleSaveReminders = async () => {
    setReminderSaving(true);
    try {
      await reminderScheduleApi.update(reminderSchedule);
      setReminderSaved(true);
      setTimeout(() => setReminderSaved(false), 2000);
    } catch {
      // silent
    } finally {
      setReminderSaving(false);
    }
  };

  const addAppointmentTime = (mins: number) => {
    setReminderSchedule((prev) => ({
      ...prev,
      appointmentReminders: {
        ...prev.appointmentReminders,
        reminderTimes: prev.appointmentReminders.reminderTimes.includes(mins)
          ? prev.appointmentReminders.reminderTimes
          : [...prev.appointmentReminders.reminderTimes, mins].sort((a, b) => b - a),
      },
    }));
  };

  const removeAppointmentTime = (mins: number) => {
    setReminderSchedule((prev) => ({
      ...prev,
      appointmentReminders: {
        ...prev.appointmentReminders,
        reminderTimes: prev.appointmentReminders.reminderTimes.filter((t) => t !== mins),
      },
    }));
  };

  const smsLength = selectedTemplate?.smsMessage?.length ?? 0;
  const smsSegments = Math.ceil(smsLength / 160) || 1;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--color-bg, #F4F6F8)" }}>
        <div className="animate-spin h-8 w-8 rounded-full border-4" style={{ borderColor: "var(--color-primary)", borderTopColor: "transparent" }} />
      </div>
    );
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--color-bg, #F4F6F8)" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate("/settings")}
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Settings
          </button>
          <h1 className="text-2xl font-bold text-slate-900">Message & Notification Settings</h1>
          <p className="text-sm text-slate-500 mt-1">
            Customize your outgoing messages and configure automated reminders
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white rounded-xl p-1 shadow-sm border border-slate-200 w-fit">
          {(["templates", "reminders"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`relative px-5 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                activeTab === tab ? "text-white" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {activeTab === tab && (
                <motion.div
                  layoutId="tab-bg"
                  className="absolute inset-0 rounded-lg"
                  style={{ backgroundColor: "var(--color-primary)" }}
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                />
              )}
              <span className="relative flex items-center gap-2">
                {tab === "templates" ? <FileText className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                {tab === "templates" ? "Message Templates" : "Reminder Schedule"}
              </span>
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {activeTab === "templates" ? (
            <motion.div
              key="templates"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
            >
              {/* Templates Tab */}
              <div className="flex gap-6">
                {/* Left sidebar - template list */}
                <div className="w-80 flex-shrink-0">
                  <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-200">
                      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Templates</h3>
                    </div>
                    <div className="py-1">
                      {TEMPLATE_CONFIGS.map((config, idx) => {
                        const Icon = config.icon;
                        const isActive = selectedKey === config.key;
                        const tmpl = templates[config.key];
                        return (
                          <motion.button
                            key={config.key}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.05 }}
                            onClick={() => setSelectedKey(config.key)}
                            className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-all ${
                              isActive
                                ? "bg-white border-l-4 shadow-sm"
                                : "border-l-4 border-transparent hover:bg-white/60"
                            }`}
                            style={isActive ? { borderLeftColor: "var(--color-accent)" } : {}}
                          >
                            <div
                              className={`mt-0.5 p-2 rounded-lg ${
                                isActive ? "bg-emerald-50" : "bg-slate-100"
                              }`}
                            >
                              <Icon
                                className="h-4 w-4"
                                style={{ color: isActive ? "var(--color-accent)" : "#64748b" }}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className={`text-sm font-medium ${isActive ? "text-slate-900" : "text-slate-700"}`}>
                                  {config.name}
                                </span>
                                {tmpl?.isCustomized && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                                    Customized
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-500 mt-0.5 truncate">{config.description}</p>
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Right panel - template editor */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={selectedKey}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="flex-1 min-w-0"
                  >
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200">
                      {/* Template header */}
                      <div className="px-8 py-6 border-b border-slate-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-xl bg-emerald-50">
                              <selectedConfig.icon className="h-5 w-5" style={{ color: "var(--color-accent)" }} />
                            </div>
                            <div>
                              <h2 className="text-lg font-semibold text-slate-900">{selectedConfig.name}</h2>
                              <p className="text-sm text-slate-500">{selectedConfig.description}</p>
                            </div>
                          </div>
                          {selectedTemplate?.isCustomized && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                              <AlertCircle className="h-3 w-3" />
                              Customized
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="px-8 py-6 space-y-6">
                        {/* Info bar */}
                        <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-blue-50 border border-blue-100">
                          <Info className="h-4 w-4 text-blue-500 flex-shrink-0" />
                          <p className="text-xs text-blue-700">
                            Use <code className="px-1 py-0.5 bg-blue-100 rounded text-blue-800 font-mono text-[11px]">{"{{variableName}}"}</code> to insert dynamic content. Click a variable below to insert it at your cursor.
                          </p>
                        </div>

                        {/* Available Variables */}
                        <div>
                          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                            Available Variables
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {selectedConfig.variables.map((v) => (
                              <motion.button
                                key={v.key}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => {
                                  const lastFocused = document.activeElement;
                                  if (lastFocused === smsRef.current) insertVariable(v.key, "sms");
                                  else if (lastFocused === subjectRef.current) insertVariable(v.key, "subject");
                                  else insertVariable(v.key, "body");
                                }}
                                className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-3 py-1 text-xs cursor-pointer hover:bg-emerald-100 transition-colors"
                              >
                                <span className="font-mono">{`{{${v.key}}}`}</span>
                              </motion.button>
                            ))}
                          </div>
                        </div>

                        {/* Email Subject */}
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                            <Mail className="h-4 w-4 text-slate-400" />
                            Email Subject
                          </label>
                          <input
                            ref={subjectRef}
                            type="text"
                            value={selectedTemplate?.emailSubject ?? ""}
                            onChange={(e) => updateField("emailSubject", e.target.value)}
                            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 transition-all"
                            placeholder="Email subject line..."
                          />
                        </div>

                        {/* Email Body */}
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                            <FileText className="h-4 w-4 text-slate-400" />
                            Email Body
                          </label>
                          <textarea
                            ref={bodyRef}
                            value={selectedTemplate?.bodyText ?? ""}
                            onChange={(e) => updateField("bodyText", e.target.value)}
                            rows={10}
                            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 transition-all resize-none font-[inherit] leading-relaxed"
                            placeholder="Email body text..."
                          />
                        </div>

                        {/* SMS Message */}
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                            <Smartphone className="h-4 w-4 text-slate-400" />
                            SMS Message
                          </label>
                          <div className="relative">
                            <textarea
                              ref={smsRef}
                              value={selectedTemplate?.smsMessage ?? ""}
                              onChange={(e) => updateField("smsMessage", e.target.value)}
                              rows={3}
                              className="w-full rounded-xl border border-slate-200 px-4 py-3 pr-20 text-sm focus:outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 transition-all resize-none"
                              placeholder="SMS message..."
                            />
                            <div className={`absolute bottom-3 right-3 text-xs font-medium ${
                              smsLength > 160 ? "text-red-500" : smsLength > 140 ? "text-amber-500" : "text-slate-400"
                            }`}>
                              {smsLength}/160
                              {smsSegments > 1 && (
                                <span className="ml-1 text-slate-400">({smsSegments} segments)</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Action buttons */}
                        <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                          <button
                            onClick={handleSave}
                            disabled={saving}
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold transition-all hover:brightness-110 disabled:opacity-50"
                            style={{ backgroundColor: saved ? "var(--color-accent)" : "var(--color-primary)" }}
                          >
                            {saved ? (
                              <>
                                <Check className="h-4 w-4" />
                                Saved
                              </>
                            ) : saving ? (
                              <>
                                <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Saving...
                              </>
                            ) : (
                              <>
                                <Save className="h-4 w-4" />
                                Save Template
                              </>
                            )}
                          </button>
                          <button
                            onClick={handlePreview}
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                          >
                            <Eye className="h-4 w-4" />
                            Preview
                          </button>
                          {selectedTemplate?.isCustomized && (
                            <button
                              onClick={handleReset}
                              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors ml-auto"
                            >
                              <RotateCcw className="h-4 w-4" />
                              Reset to Default
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="reminders"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="space-y-6"
            >
              {/* Invoice Reminders */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200">
                <div className="px-8 py-6 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-xl bg-emerald-50">
                        <Bell className="h-5 w-5" style={{ color: "var(--color-accent)" }} />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-slate-900">Invoice Reminders</h2>
                        <p className="text-sm text-slate-500">Automatically remind clients about unpaid invoices</p>
                      </div>
                    </div>
                    <ToggleSwitch
                      checked={reminderSchedule.invoiceReminders.enabled}
                      onChange={(val) =>
                        setReminderSchedule((prev) => ({
                          ...prev,
                          invoiceReminders: { ...prev.invoiceReminders, enabled: val },
                        }))
                      }
                    />
                  </div>
                </div>

                <AnimatePresence>
                  {reminderSchedule.invoiceReminders.enabled && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-8 py-6 space-y-6">
                        {/* First Reminder */}
                        <ConfigCard
                          label="First Reminder"
                          description="Send first reminder after invoice is sent"
                          value={reminderSchedule.invoiceReminders.firstReminderHours}
                          unit="hours"
                          presets={[12, 24, 48]}
                          presetLabels={["12h", "24h", "48h"]}
                          onChange={(v) =>
                            setReminderSchedule((prev) => ({
                              ...prev,
                              invoiceReminders: { ...prev.invoiceReminders, firstReminderHours: v },
                            }))
                          }
                        />

                        {/* Follow-up interval */}
                        <ConfigCard
                          label="Follow-up Reminders"
                          description="Send follow-up reminders every"
                          value={reminderSchedule.invoiceReminders.followUpDays}
                          unit="days"
                          presets={[2, 3, 5, 7]}
                          presetLabels={["2 days", "3 days", "5 days", "7 days"]}
                          onChange={(v) =>
                            setReminderSchedule((prev) => ({
                              ...prev,
                              invoiceReminders: { ...prev.invoiceReminders, followUpDays: v },
                            }))
                          }
                        />

                        {/* Max reminders */}
                        <ConfigCard
                          label="Maximum Reminders"
                          description="Stop sending after"
                          value={reminderSchedule.invoiceReminders.maxReminders}
                          unit="reminders"
                          presets={[2, 3, 5]}
                          presetLabels={["2", "3", "5"]}
                          onChange={(v) =>
                            setReminderSchedule((prev) => ({
                              ...prev,
                              invoiceReminders: { ...prev.invoiceReminders, maxReminders: v },
                            }))
                          }
                        />

                        {/* Channels */}
                        <div className="flex items-center gap-6 px-4 py-4 rounded-xl bg-slate-50 border border-slate-100">
                          <span className="text-sm font-medium text-slate-700">Channels:</span>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={reminderSchedule.invoiceReminders.channels.email}
                              onChange={(e) =>
                                setReminderSchedule((prev) => ({
                                  ...prev,
                                  invoiceReminders: {
                                    ...prev.invoiceReminders,
                                    channels: { ...prev.invoiceReminders.channels, email: e.target.checked },
                                  },
                                }))
                              }
                              className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                            />
                            <Mail className="h-4 w-4 text-slate-500" />
                            <span className="text-sm text-slate-700">Email</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={reminderSchedule.invoiceReminders.channels.sms}
                              onChange={(e) =>
                                setReminderSchedule((prev) => ({
                                  ...prev,
                                  invoiceReminders: {
                                    ...prev.invoiceReminders,
                                    channels: { ...prev.invoiceReminders.channels, sms: e.target.checked },
                                  },
                                }))
                              }
                              className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                            />
                            <Smartphone className="h-4 w-4 text-slate-500" />
                            <span className="text-sm text-slate-700">SMS</span>
                          </label>
                        </div>

                        {/* No phone number warning */}
                        {hasPhoneNumbers === false && reminderSchedule.invoiceReminders.channels.sms && (
                          <div className="flex items-center gap-2.5 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
                            <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                            <p className="text-sm text-amber-700">
                              You need to{" "}
                              <button
                                onClick={() => navigate("/messages")}
                                className="font-semibold underline hover:text-amber-900 transition-colors"
                              >
                                add a phone number
                              </button>
                              {" "}to send SMS reminders.
                            </p>
                          </div>
                        )}

                        {/* Weekend toggle */}
                        <div className="flex items-center justify-between px-4 py-4 rounded-xl bg-slate-50 border border-slate-100">
                          <div>
                            <p className="text-sm font-medium text-slate-700">Send on Weekends</p>
                            <p className="text-xs text-slate-500">Allow reminders to be sent on Saturday and Sunday</p>
                          </div>
                          <ToggleSwitch
                            checked={reminderSchedule.invoiceReminders.sendOnWeekends}
                            onChange={(val) =>
                              setReminderSchedule((prev) => ({
                                ...prev,
                                invoiceReminders: { ...prev.invoiceReminders, sendOnWeekends: val },
                              }))
                            }
                          />
                        </div>

                        {/* Visual Timeline */}
                        <div className="mt-4">
                          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                            Reminder Timeline Preview
                          </label>
                          <InvoiceTimeline schedule={reminderSchedule.invoiceReminders} />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Appointment Reminders */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200">
                <div className="px-8 py-6 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-xl bg-emerald-50">
                        <Calendar className="h-5 w-5" style={{ color: "var(--color-accent)" }} />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-slate-900">Appointment Reminders</h2>
                        <p className="text-sm text-slate-500">Send reminders before scheduled appointments</p>
                      </div>
                    </div>
                    <ToggleSwitch
                      checked={reminderSchedule.appointmentReminders.enabled}
                      onChange={(val) =>
                        setReminderSchedule((prev) => ({
                          ...prev,
                          appointmentReminders: { ...prev.appointmentReminders, enabled: val },
                        }))
                      }
                    />
                  </div>
                </div>

                <AnimatePresence>
                  {reminderSchedule.appointmentReminders.enabled && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-8 py-6 space-y-6">
                        {/* Reminder Times */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-3">
                            Reminder Times (before appointment)
                          </label>
                          <div className="flex flex-wrap gap-2 mb-3">
                            {reminderSchedule.appointmentReminders.reminderTimes.map((mins) => (
                              <motion.span
                                key={mins}
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.8, opacity: 0 }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"
                              >
                                {formatMinutes(mins)}
                                <button
                                  onClick={() => removeAppointmentTime(mins)}
                                  className="hover:text-red-500 transition-colors"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </motion.span>
                            ))}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {APPOINTMENT_PRESETS.map((p) => {
                              const isAdded = reminderSchedule.appointmentReminders.reminderTimes.includes(p.value);
                              return (
                                <button
                                  key={p.value}
                                  onClick={() => addAppointmentTime(p.value)}
                                  disabled={isAdded}
                                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                                    isAdded
                                      ? "bg-slate-50 text-slate-400 border-slate-100 cursor-not-allowed"
                                      : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                                  }`}
                                >
                                  {isAdded ? <Check className="h-3 w-3 inline mr-1" /> : <Plus className="h-3 w-3 inline mr-1" />}
                                  {p.label}
                                </button>
                              );
                            })}
                            {/* Custom time */}
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                value={customTimeInput}
                                onChange={(e) => setCustomTimeInput(e.target.value)}
                                placeholder="Custom"
                                min={1}
                                className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 text-xs focus:outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600"
                              />
                              <span className="text-xs text-slate-500">min</span>
                              <button
                                onClick={() => {
                                  const val = parseInt(customTimeInput);
                                  if (val > 0) {
                                    addAppointmentTime(val);
                                    setCustomTimeInput("");
                                  }
                                }}
                                className="px-2 py-1.5 rounded-lg text-xs font-medium text-white transition-colors"
                                style={{ backgroundColor: "var(--color-primary)" }}
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Channels */}
                        <div className="flex items-center gap-6 px-4 py-4 rounded-xl bg-slate-50 border border-slate-100">
                          <span className="text-sm font-medium text-slate-700">Channels:</span>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={reminderSchedule.appointmentReminders.channels.email}
                              onChange={(e) =>
                                setReminderSchedule((prev) => ({
                                  ...prev,
                                  appointmentReminders: {
                                    ...prev.appointmentReminders,
                                    channels: { ...prev.appointmentReminders.channels, email: e.target.checked },
                                  },
                                }))
                              }
                              className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                            />
                            <Mail className="h-4 w-4 text-slate-500" />
                            <span className="text-sm text-slate-700">Email</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={reminderSchedule.appointmentReminders.channels.sms}
                              onChange={(e) =>
                                setReminderSchedule((prev) => ({
                                  ...prev,
                                  appointmentReminders: {
                                    ...prev.appointmentReminders,
                                    channels: { ...prev.appointmentReminders.channels, sms: e.target.checked },
                                  },
                                }))
                              }
                              className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                            />
                            <Smartphone className="h-4 w-4 text-slate-500" />
                            <span className="text-sm text-slate-700">SMS</span>
                          </label>
                        </div>

                        {/* No phone number warning */}
                        {hasPhoneNumbers === false && reminderSchedule.appointmentReminders.channels.sms && (
                          <div className="flex items-center gap-2.5 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
                            <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                            <p className="text-sm text-amber-700">
                              You need to{" "}
                              <button
                                onClick={() => navigate("/messages")}
                                className="font-semibold underline hover:text-amber-900 transition-colors"
                              >
                                add a phone number
                              </button>
                              {" "}to send SMS reminders.
                            </p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Save button */}
              <div className="flex justify-end">
                <button
                  onClick={handleSaveReminders}
                  disabled={reminderSaving}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white text-sm font-bold transition-all hover:brightness-110 disabled:opacity-50"
                  style={{ backgroundColor: reminderSaved ? "var(--color-accent)" : "var(--color-primary)" }}
                >
                  {reminderSaved ? (
                    <>
                      <Check className="h-4 w-4" />
                      Saved
                    </>
                  ) : reminderSaving ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Save Reminder Settings
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Preview Modal */}
      <PremiumModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title="Template Preview"
        subtitle={`${selectedConfig.name} — with sample data`}
        icon={<Eye className="h-5 w-5" style={{ color: "var(--color-accent)" }} />}
        size="lg"
      >
        <div className="space-y-4">
          {/* Preview tabs */}
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
            {(["email", "sms"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setPreviewTab(tab)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  previewTab === tab ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {tab === "email" ? (
                  <span className="flex items-center gap-2"><Mail className="h-4 w-4" /> Email</span>
                ) : (
                  <span className="flex items-center gap-2"><Smartphone className="h-4 w-4" /> SMS</span>
                )}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            {previewTab === "email" ? (
              <motion.div
                key="email-preview"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
                    <p className="text-xs text-slate-500">
                      <span className="font-medium">Subject:</span>{" "}
                      {renderWithVariables(selectedTemplate?.emailSubject ?? "", SAMPLE_VARIABLES)}
                    </p>
                  </div>
                  <div
                    className="p-0 bg-gray-50 min-h-[300px]"
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                  />
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="sms-preview"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex justify-center py-4"
              >
                {/* Phone mockup */}
                <div className="w-72">
                  <div className="bg-slate-900 rounded-[2rem] p-3 shadow-2xl">
                    <div className="bg-white rounded-[1.5rem] overflow-hidden">
                      {/* Phone header */}
                      <div className="bg-slate-100 px-4 py-3 flex items-center gap-2 border-b border-slate-200">
                        <div className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: "var(--color-primary)" }}>
                          S&A
                        </div>
                        <div>
                          <p className="text-xs font-medium text-slate-900">Smith & Associates</p>
                          <p className="text-[10px] text-slate-500">SMS</p>
                        </div>
                      </div>
                      {/* Message bubble */}
                      <div className="p-4 min-h-[200px]">
                        <div className="bg-slate-100 rounded-2xl rounded-tl-md px-4 py-3 max-w-[85%]">
                          <p className="text-sm text-slate-800 leading-relaxed">
                            {renderWithVariables(selectedTemplate?.smsMessage ?? "", SAMPLE_VARIABLES)}
                          </p>
                        </div>
                        <p className="text-[10px] text-slate-400 mt-1 ml-1">
                          Now
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </PremiumModal>
    </div>
  );
};

// ─── Sub-components ──────────────────────────────────────────────────────────

interface ToggleSwitchProps {
  checked: boolean;
  onChange: (val: boolean) => void;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
      checked ? "" : "bg-slate-200"
    }`}
    style={checked ? { backgroundColor: "var(--color-accent)" } : {}}
  >
    <motion.span
      animate={{ x: checked ? 20 : 2 }}
      transition={{ type: "spring", stiffness: 500, damping: 30 }}
      className="inline-block h-5 w-5 rounded-full bg-white shadow-sm"
    />
  </button>
);

interface ConfigCardProps {
  label: string;
  description: string;
  value: number;
  unit: string;
  presets: number[];
  presetLabels: string[];
  onChange: (value: number) => void;
}

const ConfigCard: React.FC<ConfigCardProps> = ({ label, description, value, unit, presets, presetLabels, onChange }) => (
  <div className="px-4 py-4 rounded-xl bg-slate-50 border border-slate-100">
    <div className="flex items-center justify-between mb-3">
      <div>
        <p className="text-sm font-medium text-slate-700">{label}</p>
        <p className="text-xs text-slate-500">{description}</p>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value) || 0)}
          min={1}
          className="w-16 px-2 py-1.5 rounded-lg border border-slate-200 text-sm text-center focus:outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600"
        />
        <span className="text-xs text-slate-500">{unit}</span>
      </div>
    </div>
    <div className="flex gap-2">
      {presets.map((p, i) => (
        <button
          key={p}
          onClick={() => onChange(p)}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
            value === p
              ? "text-white border-transparent shadow-sm"
              : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
          }`}
          style={value === p ? { backgroundColor: "var(--color-primary)" } : {}}
        >
          {presetLabels[i]}
        </button>
      ))}
    </div>
  </div>
);

interface InvoiceTimelineProps {
  schedule: ReminderScheduleData["invoiceReminders"];
}

const InvoiceTimeline: React.FC<InvoiceTimelineProps> = ({ schedule }) => {
  const nodes: { label: string; sublabel: string }[] = [
    { label: "Invoice Sent", sublabel: "Day 0" },
  ];

  let cumulativeHours = schedule.firstReminderHours;
  for (let i = 0; i < schedule.maxReminders; i++) {
    const dayLabel = cumulativeHours < 24
      ? `${cumulativeHours}h later`
      : `Day ${Math.round(cumulativeHours / 24)}`;
    nodes.push({
      label: i === 0 ? "1st Reminder" : i === 1 ? "2nd Reminder" : `${i + 1}${i + 1 === 3 ? "rd" : "th"} Reminder`,
      sublabel: dayLabel,
    });
    cumulativeHours += schedule.followUpDays * 24;
  }

  nodes.push({ label: "Stop", sublabel: `Max ${schedule.maxReminders}` });

  return (
    <div className="px-4 py-5 rounded-xl bg-gradient-to-r from-slate-50 to-emerald-50/30 border border-slate-100">
      <div className="flex items-center">
        {nodes.map((node, idx) => (
          <React.Fragment key={idx}>
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: idx * 0.1 }}
              className="flex flex-col items-center flex-shrink-0"
            >
              <div
                className={`h-4 w-4 rounded-full border-2 ${
                  idx === 0
                    ? "border-emerald-500 bg-emerald-500"
                    : idx === nodes.length - 1
                    ? "border-slate-300 bg-slate-100"
                    : "border-emerald-400 bg-white"
                }`}
              />
              <p className="text-[11px] font-medium text-slate-700 mt-2 whitespace-nowrap">{node.label}</p>
              <p className="text-[10px] text-slate-500 whitespace-nowrap">{node.sublabel}</p>
            </motion.div>
            {idx < nodes.length - 1 && (
              <div className="flex-1 mx-1 mb-6">
                <div className="h-0.5 w-full bg-gradient-to-r from-emerald-300 to-emerald-200 rounded-full" />
                {idx === 0 ? (
                  <p className="text-[9px] text-slate-400 text-center mt-0.5">{schedule.firstReminderHours}h</p>
                ) : idx < nodes.length - 1 ? (
                  <p className="text-[9px] text-slate-400 text-center mt-0.5">{idx < nodes.length - 2 ? `${schedule.followUpDays}d` : ""}</p>
                ) : null}
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default MessageTemplatesPage;
